import React, {useState} from "react";
import {Box, Flex, Text, Badge, Stack, Button, useToast} from "@chakra-ui/react";
import {LuCalendarPlus} from "react-icons/lu";
import {fetchDependentAppointments, startDependentBooking, messageFor} from "../../api/dependents";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell, {EmptyState, ScopeNote} from "./DependentShell";
import {
  CancelAppointmentDialog,
  RescheduleAppointmentDialog,
} from "./DependentAppointmentActions";
import {formatDateTime} from "./format";

// Every act on this screen is a DELEGATION, never a reimplementation.
//
// Booking opens the same wizard a patient uses. Cancelling and moving go to the
// appointment's own canonical endpoints — the ones the guardian's personal
// appointments use — so the cancellation window, the fee, the availability
// engine and the notification fan-out all stay where they already live. A
// second copy of any of them inside a guardian screen is the parallel flow this
// whole initiative exists to remove.
//
// The buttons decide only whether to OFFER an action. The server checks the
// authority again when it is taken, which is why a stale tab is harmless: the
// capability list this screen read may have been true an hour ago, and the
// answer that matters is the one given at the moment of the act.

export default function DependentAppointments({state}) {
  const section = useDependentSection({
    state,
    section: "appointments",
    load: fetchDependentAppointments,
  });

  const items = section.data?.items || [];
  // The dependent's own site. Used for the parts of the screen that are about
  // the dependent and not about one appointment; a ROW uses its own.
  const homeTimeZone = section.data?.timeZone || null;
  // True when every appointment on screen is on the same clock. When it is not,
  // "your clinic's local time" is a sentence with no single referent, and the
  // hour has to name the site it belongs to.
  const oneClock = new Set(
    items.map((appointment) => appointment.timeZone || homeTimeZone)
  ).size <= 1;
  const toast = useToast();
  const [booking, setBooking] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [moving, setMoving] = useState(null);

  const capabilities = section.dependent?.capabilities?.appointments || [];
  const mayBook = capabilities.includes("book");
  const mayCancel = capabilities.includes("cancel");
  const mayModify = capabilities.includes("modify");

  const book = async () => {
    setBooking(true);
    try {
      const {bookingUrl} = await startDependentBooking(state, section.dependentPatientId);
      window.location.assign(bookingUrl);
    } catch (error) {
      toast({
        status: "error",
        title: "Booking could not be started",
        description: messageFor(error, "Please try again, or contact your clinic."),
        duration: 6000,
        isClosable: true,
      });
      setBooking(false);
    }
  };

  return (
    <DependentShell
      title="Appointments"
      subtitle={`${section.displayName}'s appointments`}
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
      actions={
        mayBook &&
        section.status === SECTION_STATES.READY && (
          <Button
            size="sm"
            colorScheme="orange"
            leftIcon={<LuCalendarPlus />}
            isLoading={booking}
            onClick={book}
          >
            Book an appointment
          </Button>
        )
      }
    >
      {section.status === SECTION_STATES.READY &&
        (items.length === 0 ? (
          <EmptyState
            heading="No appointments"
            body={
              section.data?.recordScope === "own"
                ? "You have not booked any appointments for this person."
                : "There are no appointments on this record."
            }
          />
        ) : (
          <>
            <Stack spacing={3}>
              {items.map((appointment) => (
                <Row
                  key={appointment.appointmentId}
                  appointment={appointment}
                  timeZone={appointment.timeZone || homeTimeZone}
                  showZoneName={!oneClock}
                  onCancel={mayCancel ? () => setCancelling(appointment) : null}
                  onReschedule={mayModify ? () => setMoving(appointment) : null}
                />
              ))}
            </Stack>
            <ScopeNote scope={section.data?.recordScope} noun="appointments" />
            {homeTimeZone && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                {oneClock
                  ? "Times are shown in your clinic's local time."
                  : "Each time is shown in the local time of the clinic it is at."}
              </Text>
            )}
          </>
        ))}

      {cancelling && (
        <CancelAppointmentDialog
          state={state}
          appointment={cancelling}
          displayName={section.displayName}
          timeZone={cancelling.timeZone || homeTimeZone}
          isOpen
          onClose={() => setCancelling(null)}
          onDone={section.reload}
        />
      )}

      {moving && (
        <RescheduleAppointmentDialog
          state={state}
          appointment={moving}
          displayName={section.displayName}
          timeZone={moving.timeZone || homeTimeZone}
          isOpen
          onClose={() => setMoving(null)}
          onDone={section.reload}
        />
      )}
    </DependentShell>
  );
}

function Row({appointment, timeZone, showZoneName, onCancel, onReschedule}) {
  const past = appointment.startsAt && Number(appointment.startsAt) < Date.now();
  // A closed appointment cannot be changed, whatever the guardian holds: moving
  // it would erase the record of what happened, and any fee attached to it. The
  // server refuses either way; not offering the button is how the screen stays
  // honest about what is possible.
  const open = !appointment.cancelled && !appointment.noShow && !past;

  return (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius="10px"
      p={4}
      bg="white"
      justify="space-between"
      align={{base: "flex-start", md: "center"}}
      direction={{base: "column", md: "row"}}
      gap={3}
      opacity={appointment.cancelled ? 0.6 : 1}
    >
      <Box minW={0}>
        <Text fontWeight="600" color="#1a1f36">
          {appointment.serviceName || "Appointment"}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {formatDateTime(appointment.startsAt, timeZone, {
            withZoneName: showZoneName,
          })}
          {appointment.clinicianName ? ` · ${appointment.clinicianName}` : ""}
          {appointment.locationName ? ` · ${appointment.locationName}` : ""}
        </Text>
      </Box>
      <Flex gap={2} wrap="wrap" flexShrink={0} align="center">
        {appointment.cancelled && <Tag colorScheme="red">Cancelled</Tag>}
        {appointment.noShow && <Tag colorScheme="orange">No show</Tag>}
        {!appointment.cancelled && appointment.confirmed && (
          <Tag colorScheme="green">Confirmed</Tag>
        )}
        {!appointment.cancelled && !past && !appointment.confirmed && (
          <Tag colorScheme="blue">Upcoming</Tag>
        )}
        {appointment.bookedByYou && <Tag colorScheme="purple">Booked by you</Tag>}

        {open && onReschedule && (
          <Button size="xs" variant="outline" onClick={onReschedule}>
            Move
          </Button>
        )}
        {open && onCancel && (
          <Button size="xs" variant="outline" colorScheme="red" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Flex>
    </Flex>
  );
}

function Tag({children, colorScheme}) {
  return (
    <Badge colorScheme={colorScheme} fontSize="10px" textTransform="none" borderRadius="full" px={2}>
      {children}
    </Badge>
  );
}
